import { motion } from "framer-motion";
import { Shield, Users, MapPin, Award, Heart, Target, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const stats = [
  { value: "10,000+", label: "Happy Customers" },
  { value: "50+", label: "Vehicles" },
  { value: "100+", label: "Verified Drivers" },
  { value: "4.9★", label: "Google Rating" },
];

const About = () => (
  <Layout>
    <img
      src="/trichy_rockfort_taxi_hero_1776155164304.png"
      alt="trichy to velankanni taxi trusted cab service in trichy local and outstation taxi service"
      className="hidden"
    />
    <SEOHead
      title="Trichy to Velankanni Taxi - Trusted Cab Service in Trichy"
      description="Learn about Trichy to Velankanni Taxi — Trichy's most trusted cab service. Our mission, values, and commitment to safe, affordable travel across Trichy."
      keywords="taxi service in trichy, trichy taxi, cab service trichy, best taxi in trichy, local and outstation taxi trichy"
    />

    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          <span className="text-gradient">Trichy to Velankanni Taxi</span>
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Your trusted partner for safe, affordable, and reliable taxi services across Trichy and South India.
        </motion.p>
      </div>
    </section>

    {/* Stats */}
    <section className="section-padding">
      <div className="container-custom">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }} className="glass-card rounded-2xl p-6 text-center">
              <p className="text-3xl font-bold text-gradient">{s.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Story */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <motion.div {...fadeUp}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mb-4">Our Story</span>
            <h2 className="text-3xl font-bold text-foreground mb-4">Built on Trust, Driven by Service</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Trichy to Velankanni Taxi was founded with a simple mission: to provide the people of Trichy and visitors to this historic city with a taxi service they can trust completely. Starting with a passion for excellent service, we've grown to become Trichy's most recommended cab service provider.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our deep local expertise sets us apart. Our drivers aren't just skilled behind the wheel — they're ambassadors of Trichy who know every street, every landmark, and every hidden gem in this temple city. From the majestic Rockfort Temple to the spiritual serenity of Srirangam, our team ensures every ride is more than just transportation — it's an experience.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Today, we serve thousands of happy customers annually, maintain a fleet of well-kept vehicles, and employ background-verified drivers. Whether it's an Airport pickup / Airport drop or a long-distance journey to Madurai, Tanjore, or Kodaikanal — Trichy to Velankanni Taxi is the name that locals and tourists trust.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
            <img 
              src="/velankanni_hero.png" 
              alt="Trichy to Velankanni Taxi" 
              className="relative z-10 rounded-2xl shadow-2xl border border-white/10" 
            />
          </motion.div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <motion.div {...fadeUp} className="glass-card-hover rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Target className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">Our Mission</h3>
            <p className="text-muted-foreground leading-relaxed">
              To make safe, affordable, and reliable transportation accessible to every traveler in Trichy. We aim to set the gold standard for taxi services in Trichy by combining technology with personal touch.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="glass-card-hover rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Heart className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">Our Vision</h3>
            <p className="text-muted-foreground leading-relaxed">
              To be the first name that comes to mind when anyone thinks of taxi services in Trichy. We envision a future where every journey in Trichy is comfortable, safe, and memorable.
            </p>
          </motion.div>
        </div>

        {/* Why Trust Us */}
        <SectionHeading badge="Trust" title="Why Customers" highlight="Trust Us" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Verified Drivers", desc: "Every driver undergoes thorough background checks and professional training before joining our team." },
            { icon: Award, title: "Top-Rated Service", desc: "4.9-star rating on Google with thousands of positive reviews from satisfied customers." },
            { icon: Users, title: "Local Expertise", desc: "Our team has deep knowledge of Trichy's roads, attractions, and best routes." },
            { icon: MapPin, title: "Wide Coverage", desc: "From local Trichy rides to outstation trips across South India, we cover it all." },
            { icon: CheckCircle2, title: "Transparent Pricing", desc: "No hidden charges, no surge pricing. Fixed, fair rates for every journey." },
            { icon: Heart, title: "Customer First", desc: "Your comfort, safety, and satisfaction are our top priorities on every single ride." },
          ].map((item, i) => (
            <motion.div key={item.title} {...fadeUp} transition={{ delay: i * 0.1 }} className="glass-card-hover rounded-2xl p-6">
              <item.icon className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-foreground font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </Layout>
);

export default About;
